// src/lib/labelPrinterUtils.ts

interface PrintItem {
  name: string;
  barcode: string;
  qty: number;
  barcodeType?: "code128" | "qr" | "both";
  storeName?: string;
  salePrice?: number;
  mrp?: number;
}

/**
 * Generates a TSPL (TSC Printer Language) byte array for label printing.
 * Dynamically scales to any sticker size (widthMm x heightMm).
 * Supports 1D (Code 128), 2D (QR Code), or both.
 */
export function buildBarcodeTSPL(printList: PrintItem[], widthMm: number = 50, heightMm: number = 25): Uint8Array {
  let commandString = "";

  commandString += `SIZE ${widthMm} mm, ${heightMm} mm\r\n`;
  commandString += "GAP 2 mm, 0\r\n";
  commandString += "DIRECTION 1\r\n";
  commandString += "CODEPAGE UTF-8\r\n";

  const dpi = 8;

  printList.forEach((item) => {
    if (item.qty <= 0) return;

    const type = item.barcodeType ?? "code128";

    if (type === "code128" || type === "both") {
      const textY = 15;
      const barcodeY = textY + 40;
      const barcodeHeight = Math.max(40, Math.floor((heightMm * dpi) - barcodeY - 20));

      commandString += "CLS\r\n";
      const maxTextChars = Math.floor(widthMm / 1.5);
      commandString += `TEXT 15,${textY},"3",0,1,1,"${item.name.substring(0, maxTextChars)}"\r\n`;
      commandString += `BARCODE 15,${barcodeY},"128",${barcodeHeight},1,0,2,2,"${item.barcode}"\r\n`;
      commandString += `PRINT ${item.qty},1\r\n`;
    }

    if (type === "qr" || type === "both") {
      commandString += "CLS\r\n";

      const qrMaxDot = Math.min(heightMm * dpi * 0.8, widthMm * dpi * 0.35);
      const qrModule = Math.max(2, Math.min(8, Math.floor(qrMaxDot / 25)));
      const qrWidthDots = 25 * qrModule;
      const qrX = 15;
      const qrY = Math.max(10, Math.floor((heightMm * dpi - qrWidthDots) / 2));

      const textX = qrX + qrWidthDots + 15;
      const maxTextW = Math.max(4, Math.floor((widthMm * dpi - textX - 10) / 12));

      commandString += `QRCODE ${qrX},${qrY},H,${qrModule},A,0,"${item.barcode}"\r\n`;

      let currentY = Math.max(10, qrY - 5);
      const ySpacing = Math.floor(heightMm * dpi / 5.5);

      if (item.storeName) {
        commandString += `TEXT ${textX},${currentY},"2",0,1,1,"${item.storeName.substring(0, maxTextW)}"\r\n`;
        currentY += ySpacing;
      }

      commandString += `TEXT ${textX},${currentY},"2",0,1,1,"${item.name.substring(0, maxTextW)}"\r\n`;
      currentY += ySpacing;

      const mrpVal = item.mrp ?? item.salePrice;
      let priceLine = "";
      if (mrpVal !== undefined) {
        priceLine += `MRP: ${Number(mrpVal).toFixed(2)} `;
      }
      if (item.salePrice !== undefined) {
        priceLine += `Sale: ${Number(item.salePrice).toFixed(2)}`;
      }
      if (priceLine) {
        commandString += `TEXT ${textX},${currentY},"2",0,1,1,"${priceLine.trim()}"\r\n`;
        currentY += ySpacing;
      }

      commandString += `TEXT ${textX},${currentY},"2",0,1,1,"${item.barcode}"\r\n`;
      commandString += `PRINT ${item.qty},1\r\n`;
    }
  });

  const encoder = new TextEncoder();
  return encoder.encode(commandString);
}

/**
 * Generates a ZPL II (Zebra Printer Language) byte array for label printing.
 * Dynamically scales to any sticker size (widthMm x heightMm).
 * Supports 1D (Code 128), 2D (QR Code), or both.
 */
export function buildBarcodeZPL(printList: PrintItem[], widthMm: number = 50, heightMm: number = 25): Uint8Array {
  let commandString = "";
  const dpi = 8;

  printList.forEach((item) => {
    if (item.qty <= 0) return;

    const type = item.barcodeType ?? "code128";
    const maxTextChars = Math.floor(widthMm / 1.5);

    if (type === "code128" || type === "both") {
      const textY = 15;
      const barcodeY = textY + 40;
      const barcodeHeight = Math.max(40, Math.floor((heightMm * dpi) - barcodeY - 20));

      commandString += "^XA\n";
      commandString += `^FO15,${textY}^A0N,25,25^FD${item.name.substring(0, maxTextChars)}^FS\n`;
      commandString += `^FO15,${barcodeY}^BCN,${barcodeHeight},Y,N,N^FD${item.barcode}^FS\n`;
      commandString += `^PQ${item.qty}\n`;
      commandString += "^XZ\n";
    }

    if (type === "qr" || type === "both") {
      const qrMaxDot = Math.min(heightMm * dpi * 0.8, widthMm * dpi * 0.35);
      const qrMagnification = Math.max(2, Math.min(8, Math.floor(qrMaxDot / 25)));

      const qrX = 15;
      const qrY = Math.max(10, Math.floor((heightMm * dpi - (25 * qrMagnification)) / 2));

      const textX = qrX + (25 * qrMagnification) + 20;
      const maxTextW = Math.max(4, Math.floor((widthMm * dpi - textX - 10) / 12));

      commandString += "^XA\n";
      commandString += `^FO${qrX},${qrY}^BQN,2,${qrMagnification}^FDQA,${item.barcode}^FS\n`;

      let currentY = Math.max(10, qrY - 5);
      const ySpacing = Math.floor(heightMm * dpi / 5.5);

      if (item.storeName) {
        commandString += `^FO${textX},${currentY}^A0N,20,20^FD${item.storeName.substring(0, maxTextW)}^FS\n`;
        currentY += ySpacing;
      }

      commandString += `^FO${textX},${currentY}^A0N,20,20^FD${item.name.substring(0, maxTextW)}^FS\n`;
      currentY += ySpacing;

      const mrpVal = item.mrp ?? item.salePrice;
      let priceLine = "";
      if (mrpVal !== undefined) {
        priceLine += `MRP: ${Number(mrpVal).toFixed(2)} `;
      }
      if (item.salePrice !== undefined) {
        priceLine += `Sale: ${Number(item.salePrice).toFixed(2)}`;
      }
      if (priceLine) {
        commandString += `^FO${textX},${currentY}^A0N,20,20^FD${priceLine.trim()}^FS\n`;
        currentY += ySpacing;
      }

      commandString += `^FO${textX},${currentY}^A0N,20,20^FD${item.barcode}^FS\n`;
      commandString += `^PQ${item.qty}\n`;
      commandString += "^XZ\n";
    }
  });

  const encoder = new TextEncoder();
  return encoder.encode(commandString);
}
